import { useEffect, useState } from "react";
import type { ConnectorOption, SyncSection } from "../types/sync-flow";

export const ROUTES = {
  DASHBOARD: "/dashboard",
  CONNECTORS: "/dashboard/connectors",
  CONNECT_ACCOUNTS: "/dashboard/connect-accounts",
  CHOOSE_SYNC: "/dashboard/choose-sync",
  MAP_FIELDS: "/dashboard/map-fields",
  SYNC_DIRECTION: "/dashboard/sync-direction",
  REVIEW_SETUP: "/dashboard/review-setup",
} as const;

export const STORAGE_KEYS = {
  CONNECTOR_FLOW_STEP: "connectorFlowStep",
  SELECTED_CONNECTOR: "selectedConnector",
  SELECTED_SYNC_OPTION: "selectedSyncOption",
  FIELD_MAPPINGS: "fieldMappings",
  SYNC_CONFIGURATION: "syncConfiguration",
  MODIFY_SYNC_CONFIGURATION: "modifysyncConfiguration",
  HUBSPOT_CONNECTED: "hubspot_connected",
  SALESFORCE_CONNECTED: "salesforce_connected",
  HUBSPOT_TENANT_ID: "hubspot_tenant_id",
  SALESFORCE_TENANT_ID: "salesforce_tenant_id",
} as const;

export const DEFAULT_CONNECTORS: ConnectorOption[] = [
  {
    id: "hubspot-salesforce",
    name: "HubSpot to Salesforce",
    description:
      "Sync and manage your contacts, leads, and deals seamlessly between HubSpot and Salesforce. Create, update, and edit records automatically to keep both platforms in sync and your sales process smooth.",
    fromPlatform: "HubSpot",
    toPlatform: "Salesforce",
    fromLogo: "https://cdn.worldvectorlogo.com/logos/hubspot-1.svg",
    toLogo: "https://cdn.worldvectorlogo.com/logos/salesforce-2.svg",
    isAvailable: true,
  },
];

const getObjectName = (name: string) => {
  const objectName = localStorage.getItem(name) || "contact";
  return (
    objectName.charAt(0).toLowerCase() + objectName.slice(1) || "contact"
  );
};

// ✅ Hook to get sync sections dynamically
export const useSyncSections = (): SyncSection[] => {
  const [sections, setSections] = useState<SyncSection[]>([]);

  useEffect(() => {
    const source = getObjectName("source_object");
    const target = getObjectName("target_object");

    const syncSections: SyncSection[] = [
      {
        id: "creating",
        title: "Hubspot sync config",
        expanded: true,
        rules: [
          {
            id: "hs-contact-create",
            sourceAction: `A ${source} is created`,
            targetAction: `Create the ${target}`,
            enabled: true,
            sourceIcon: "hubspot",
            targetIcon: "salesforce",
          },
          {
            id: "hs-contact-updates",
            sourceAction: `A ${source} is updated`,
            targetAction: `Update the ${target}`,
            enabled: true,
            sourceIcon: "hubspot",
            targetIcon: "salesforce",
          },
          {
            id: "hs-contact-delete",
            sourceAction: `A ${source} is deleted`,
            targetAction: `Delete the ${target}`,
            enabled: true,
            sourceIcon: "hubspot",
            targetIcon: "salesforce",
          },
        ],
      },
      {
        id: "deleting",
        title: "Salesforce sync config",
        expanded: true,
        rules: [
          {
            id: "sf-lead-create",
            sourceAction: `A ${target} is created`,
            targetAction: `Create the ${source}`,
            enabled: true,
            sourceIcon: "salesforce",
            targetIcon: "hubspot",
          },
          {
            id: "sf-lead-update",
            sourceAction: `A ${target} is updated`,
            targetAction: `Update the ${source}`,
            enabled: true,
            sourceIcon: "salesforce",
            targetIcon: "hubspot",
          },
          {
            id: "sf-lead-delete",
            sourceAction: `A ${target} is deleted`,
            targetAction: `Delete the ${source}`,
            enabled: true,
            sourceIcon: "salesforce",
            targetIcon: "hubspot",
          },
        ],
      },
    ];
    setSections(syncSections);
  }, []); // runs on component mount
  return sections;
};
